import { useState } from "react";
import { Navigate } from "react-router-dom";
import { Lock, Mail, ShieldCheck, Users, Zap } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { useAuth, useBranding } from "@/context/AuthContext";
import { BrandLogo } from "@/components/BrandLogo";
import { errMsg } from "@/lib/api";

const FEATURES = [
  { icon: Users, text: "Casella condivisa per tutto il team" },
  { icon: Zap, text: "Risposte rapide e messaggi automatici" },
  { icon: ShieldCheck, text: "Integrazione ufficiale WhatsApp Cloud API" },
];

export default function Login() {
  const { user, login } = useAuth();
  const { data: brand } = useBranding();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  if (user) return <Navigate to="/" replace />;

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      await login(email, password);
    } catch (err) {
      setError(errMsg(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-[1.1fr_1fr]">
      <section className="hidden lg:flex flex-col justify-between bg-[#0F172A] text-white p-14 relative overflow-hidden">
        <div className="absolute -right-32 -top-32 w-96 h-96 rounded-full border border-[#25D366]/20" />
        <div className="absolute -right-12 -top-12 w-96 h-96 rounded-full border border-[#25D366]/10" />
        <div className="flex items-center gap-3 relative">
          <BrandLogo size={40} />
          <span className="font-bold text-lg">{brand?.company_name}</span>
        </div>
        <div className="relative max-w-md fade-up">
          <p className="text-[11px] uppercase tracking-[0.2em] text-[#25D366] font-semibold mb-4">WhatsApp Business · CRM</p>
          <h1 className="text-4xl xl:text-5xl font-extrabold leading-[1.1] tracking-tight">Ogni conversazione, un unico posto.</h1>
          <p className="mt-5 text-slate-400 text-base">Gestisci i clienti WhatsApp con il tuo team, assegna le chat e tieni traccia di tutto nel CRM integrato.</p>
          <ul className="mt-10 space-y-4">
            {FEATURES.map(({ icon: Icon, text }) => (
              <li key={text} className="flex items-center gap-3 text-sm text-slate-300">
                <span className="w-8 h-8 rounded-md bg-[#1E293B] grid place-items-center"><Icon size={16} className="text-[#25D366]" /></span>
                {text}
              </li>
            ))}
          </ul>
        </div>
        <p className="text-xs text-slate-500 relative">© {new Date().getFullYear()} {brand?.company_name}</p>
      </section>
      <section className="flex items-center justify-center p-8 bg-[#F8FAFC]">
        <form onSubmit={submit} className="w-full max-w-sm fade-up" data-testid="login-form">
          <div className="lg:hidden mb-8"><BrandLogo size={44} /></div>
          <h2 className="text-2xl font-bold tracking-tight text-slate-900">Accedi</h2>
          <p className="text-sm text-slate-500 mt-1 mb-8">Inserisci le credenziali del tuo account aziendale.</p>
          <div className="space-y-4">
            <div className="space-y-1.5">
              <Label htmlFor="email">Email</Label>
              <div className="relative">
                <Mail size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                <Input id="email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} className="pl-9 h-11 bg-white" placeholder="nome@azienda.it" data-testid="login-email-input" />
              </div>
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="password">Password</Label>
              <div className="relative">
                <Lock size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                <Input id="password" type="password" required value={password} onChange={(e) => setPassword(e.target.value)} className="pl-9 h-11 bg-white" placeholder="••••••••" data-testid="login-password-input" />
              </div>
            </div>
            {error && <div data-testid="login-error" className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-md px-3 py-2">{error}</div>}
            <Button type="submit" disabled={loading} className="w-full h-11 bg-[#0F172A] hover:bg-[#1E293B] font-semibold" data-testid="login-submit-button">
              {loading ? "Accesso in corso…" : "Accedi"}
            </Button>
          </div>
        </form>
      </section>
    </div>
  );
}
